
import { AppState, Node, Zone, Wall, Agent, SimulationStats, NodeType, EditMode, ViewMode, HistoryEntry, Path, PathPoint, MapIcon, IconType, SelectionRef, PathPointSelection } from '../types';
import { doLineSegmentsIntersect, mergeOverlappingZones, isPointInRect } from '../utils/geometry';

export type AppAction =
  | { type: 'SET_EDIT_MODE'; payload: EditMode }
  | { type: 'SET_VIEW_MODE'; payload: ViewMode }
  | { type: 'SET_SELECTED_NODE_TYPE'; payload: NodeType }
  | { type: 'SET_SELECTED_ICON_TYPE'; payload: IconType }
  | { type: 'SET_SELECTION'; payload: SelectionRef[] }
  | { type: 'TOGGLE_SELECTION'; payload: SelectionRef }
  | { type: 'SELECT_PATH_POINT'; payload: PathPointSelection | null }
  | { type: 'SELECT_AGENT'; payload: string | null }
  | { type: 'TOGGLE_RUN' }
  | { type: 'RESET_SIMULATION' }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'ADD_NODE'; payload: Node }
  | { type: 'UPDATE_NODE'; payload: { id: string; updates: Partial<Node> } }
  | { type: 'DELETE_NODE'; payload: string }
  | { type: 'ADD_WALL'; payload: Wall }
  | { type: 'DELETE_WALL'; payload: string }
  | { type: 'ADD_ZONE'; payload: Zone }
  | { type: 'UPDATE_ZONE'; payload: { id: string; updates: Partial<Zone> } }
  | { type: 'DELETE_ZONE'; payload: string }
  | { type: 'MERGE_ZONES' }
  | { type: 'ADD_PATH'; payload: Path }
  | { type: 'UPDATE_PATH'; payload: { id: string; updates: Partial<Path> } }
  | { type: 'DELETE_PATH'; payload: string }
  | { type: 'TRANSFORM_ITEMS'; payload: { items: SelectionRef[]; dx: number; dy: number } }
  | { type: 'RESIZE_ZONE'; payload: { id: string; rects: Zone['rects'] } }
  | { type: 'INSERT_PATH_POINT'; payload: { pathId: string; index: number; point: PathPoint; nodeId?: string; generatedNode?: Node } }
  | { type: 'MOVE_PATH_POINT'; payload: { pathId: string; index: number; point: PathPoint; snapNodeId?: string } }
  | { type: 'DELETE_PATH_POINT'; payload: { pathId: string; index: number } }
  | { type: 'PASTE_ITEMS'; payload: { nodes: Node[]; zones: Zone[]; walls: Wall[]; paths: Path[]; icons: MapIcon[]; selection: SelectionRef[] } }
  | { type: 'DELETE_SELECTION'; payload: SelectionRef[] }
  | { type: 'ADD_POINT_TO_PATH'; payload: { pathId: string; point: PathPoint; nodeId?: string; generatedNode?: Node } }
  | { type: 'ADD_ICON'; payload: MapIcon }
  | { type: 'UPDATE_ICON'; payload: { id: string; updates: Partial<MapIcon> } }
  | { type: 'DELETE_ICON'; payload: string }
  | { type: 'REGION_DELETE'; payload: { x: number, y: number, w: number, h: number } }
  | { type: 'SET_FLOOR_PLAN'; payload: string | null }
  | { type: 'LOAD_LAYOUT'; payload: Partial<AppState> }
  | { type: 'TOGGLE_CONTROLS' }
  | { type: 'SET_SIM_SPEED'; payload: number }
  | { type: 'SET_AUTO_SPAWN'; payload: { active?: boolean, rate?: number } }
  | { type: 'SET_CALIBRATION'; payload: { points?: { x: number, y: number }[], scale?: number } }
  | { type: 'SET_MEASUREMENT_POINTS'; payload: { x: number, y: number }[] }
  | { type: 'SET_MAP_ADJUSTMENTS'; payload: { opacity?: number, brightness?: number, contrast?: number } }
  | { type: 'SET_HELP_OPEN'; payload: { open: boolean, tab?: AppState['helpTab'] } }
  | { type: 'SET_REPORT_OPEN'; payload: boolean }
  | { type: 'SET_BEHAVIOR_MODAL_OPEN'; payload: boolean }
  | { type: 'SPAWN_AGENTS'; payload: Agent[] }
  | { type: 'UPDATE_STATS'; payload: Partial<SimulationStats> }
  | { type: 'SET_SIM_CONFIG'; payload: Partial<AppState['simConfig']> }
  | { type: 'SIM_TICK'; payload: { agents: Agent[], nodes: Node[], stats: SimulationStats } };

const createHistoryEntry = (state: AppState): HistoryEntry => ({
  nodes: JSON.parse(JSON.stringify(state.nodes)),
  zones: JSON.parse(JSON.stringify(state.zones)),
  walls: JSON.parse(JSON.stringify(state.walls)),
  paths: JSON.parse(JSON.stringify(state.paths)),
  icons: JSON.parse(JSON.stringify(state.icons))
});

const pushHistory = (state: AppState): HistoryEntry[] => {
  try {
    const entry = createHistoryEntry(state);
    return [entry, ...state.history].slice(0, 30);
  } catch (e) {
    console.error("Failed to push history entry", e);
    return state.history;
  }
};

const removeNodeReferencesFromPaths = (paths: Path[], deletedNodes: Node[]): Path[] => {
  const deletedIds = new Set(deletedNodes.map(node => node.id));
  const deletedCoordinates = new Set(deletedNodes.map(node => `${node.x}:${node.y}`));

  return paths.flatMap(path => {
    const keptPoints: PathPoint[] = [];
    const keptNodeIds: Array<string | null> = [];
    path.points.forEach((point, index) => {
      const nodeId = path.nodeIds?.[index] ?? null;
      const shouldDelete = nodeId ? deletedIds.has(nodeId) : deletedCoordinates.has(`${point.x}:${point.y}`);
      if (!shouldDelete) {
        keptPoints.push(point);
        keptNodeIds.push(nodeId);
      }
    });
    if (keptPoints.length === 0) return [];
    return [{
      ...path,
      points: keptPoints,
      nodeIds: keptNodeIds,
      generatedNodeIds: path.generatedNodeIds?.filter(id => !deletedIds.has(id)),
      connections: path.connections?.filter(edge => !deletedIds.has(edge.fromId) && !deletedIds.has(edge.toId))
    }];
  });
};

const syncPathPointsToNodes = (paths: Path[], nodes: Node[]): Path[] => {
  const nodeMap = new Map(nodes.map(node => [node.id, node]));
  return paths.map(path => ({
    ...path,
    points: path.points.map((point, index) => {
      const node = path.nodeIds?.[index] ? nodeMap.get(path.nodeIds[index]!) : null;
      return node ? { x: node.x, y: node.y } : point;
    })
  }));
};

const rebuildSimulationGraph = (nodes: Node[], paths: Path[]): { nodes: Node[]; paths: Path[] } => {
  const ownedEdges = new Set(paths.flatMap(path => path.connections ?? []).flatMap(edge => [
    `${edge.fromId}:${edge.toId}`, `${edge.toId}:${edge.fromId}`
  ]));
  let rebuiltNodes = nodes.map(node => ({
    ...node,
    connections: node.connections.filter(id => !ownedEdges.has(`${node.id}:${id}`))
  }));
  const nodeMap = () => new Map(rebuiltNodes.map(node => [node.id, node]));

  const rebuiltPaths = paths.map(path => {
    if ((path.kind ?? (path.name.toLowerCase().startsWith('sim.') ? 'SIMULATION' : 'ARCHITECTURAL')) !== 'SIMULATION') return path;
    const connections: Array<{ fromId: string; toId: string }> = [];
    for (let index = 1; index < (path.nodeIds?.length ?? 0); index++) {
      const fromId = path.nodeIds?.[index - 1];
      const toId = path.nodeIds?.[index];
      if (!fromId || !toId || fromId === toId) continue;
      const map = nodeMap();
      const from = map.get(fromId);
      const to = map.get(toId);
      if (!from || !to) continue;
      if (!from.connections.includes(toId)) {
        rebuiltNodes = rebuiltNodes.map(node => {
          if (node.id === fromId) return { ...node, connections: [...node.connections, toId] };
          if (node.id === toId) return { ...node, connections: [...node.connections, fromId] };
          return node;
        });
        connections.push({ fromId, toId });
      }
    }
    return { ...path, connections };
  });
  return { nodes: rebuiltNodes, paths: syncPathPointsToNodes(rebuiltPaths, rebuiltNodes) };
};

const removeUnreferencedGeneratedNodes = (nodes: Node[], paths: Path[], candidates: string[]): Node[] => {
  const referenced = new Set(paths.flatMap(path => path.nodeIds ?? []).filter((id): id is string => Boolean(id)));
  const removable = new Set(candidates.filter(id => !referenced.has(id)));
  return nodes
    .filter(node => !removable.has(node.id))
    .map(node => ({ ...node, connections: node.connections.filter(id => !removable.has(id)) }));
};

const reduceApp = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_EDIT_MODE': 
      return {
        ...state,
        editMode: action.payload,
        selectedItems: [],
        selectedPathPoint: null,
        selectedAgentId: null,
        calibrationPoints: action.payload === 'CALIBRATE_SCALE' ? state.calibrationPoints : [],
        measurementPoints: action.payload === 'MEASURE_PATH' ? state.measurementPoints : []
      };
    
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload };

    case 'SET_SELECTED_NODE_TYPE':
      return { ...state, selectedNodeType: action.payload };

    case 'SET_SELECTED_ICON_TYPE':
      return { ...state, selectedIconType: action.payload };

    case 'SET_SELECTION':
      return { ...state, selectedItems: action.payload, selectedPathPoint: null };

    case 'TOGGLE_SELECTION': {
      const exists = state.selectedItems.some(item => item.kind === action.payload.kind && item.id === action.payload.id);
      return {
        ...state,
        selectedItems: exists
          ? state.selectedItems.filter(item => item.kind !== action.payload.kind || item.id !== action.payload.id)
          : [...state.selectedItems, action.payload],
        selectedPathPoint: null
      };
    }

    case 'SELECT_PATH_POINT':
      return { ...state, selectedPathPoint: action.payload };

    case 'SELECT_AGENT':
      return { ...state, selectedAgentId: action.payload };


    case 'ADD_NODE': 
      return {
        ...state,
        history: pushHistory(state),
        nodes: [...state.nodes, action.payload],
        layoutVersion: state.layoutVersion + 1
      };

    case 'UPDATE_NODE':
      return {
        ...state,
        history: pushHistory(state),
        nodes: state.nodes.map(n => n.id === action.payload.id ? { ...n, ...action.payload.updates } : n),
        layoutVersion: state.layoutVersion + 1
      };

    case 'DELETE_NODE': {
      const deletedNodes = state.nodes.filter(node => node.id === action.payload);
      return {
        ...state,
        history: pushHistory(state),
        nodes: state.nodes
          .map(node => ({ ...node, connections: node.connections.filter(id => id !== action.payload) }))
          .filter(node => node.id !== action.payload),
        paths: removeNodeReferencesFromPaths(state.paths, deletedNodes),
        selectedItems: state.selectedItems.filter(item => item.kind !== 'NODE' || item.id !== action.payload),
        layoutVersion: state.layoutVersion + 1
      };
    }

    case 'ADD_WALL':
      return {
        ...state,
        history: pushHistory(state),
        walls: [...state.walls, action.payload],
        layoutVersion: state.layoutVersion + 1
      };

    case 'DELETE_WALL':
      return {
        ...state,
        history: pushHistory(state),
        walls: state.walls.filter(wall => wall.id !== action.payload),
        layoutVersion: state.layoutVersion + 1
      };

    case 'ADD_ZONE':
      return {
        ...state,
        history: pushHistory(state),
        zones: [...state.zones, action.payload],
        layoutVersion: state.layoutVersion + 1
      };

    case 'UPDATE_ZONE':
      return {
        ...state,
        history: pushHistory(state),
        zones: state.zones.map(z => z.id === action.payload.id ? { ...z, ...action.payload.updates } : z),
        layoutVersion: state.layoutVersion + 1
      };

    case 'DELETE_ZONE':
      return {
        ...state,
        history: pushHistory(state),
        zones: state.zones.filter(z => z.id !== action.payload),
        selectedItems: state.selectedItems.filter(item => item.kind !== 'ZONE' || item.id !== action.payload),
        layoutVersion: state.layoutVersion + 1
      };

    case 'MERGE_ZONES':
      return {
        ...state,
        history: pushHistory(state),
        zones: mergeOverlappingZones(state.zones, state.selectedItems.find(item => item.kind === 'ZONE')?.id),
        layoutVersion: state.layoutVersion + 1
      };

    case 'ADD_ICON':
      return {
        ...state,
        history: pushHistory(state),
        icons: [...state.icons, action.payload],
        layoutVersion: state.layoutVersion + 1
      };

    case 'UPDATE_ICON':
      return {
        ...state,
        history: pushHistory(state),
        icons: state.icons.map(icon => icon.id === action.payload.id ? { ...icon, ...action.payload.updates } : icon),
        layoutVersion: state.layoutVersion + 1
      };

    case 'DELETE_ICON':
      return {
        ...state,
        history: pushHistory(state),
        icons: state.icons.filter(icon => icon.id !== action.payload),
        selectedItems: state.selectedItems.filter(item => item.kind !== 'ICON' || item.id !== action.payload),
        layoutVersion: state.layoutVersion + 1
      };

    case 'ADD_PATH':
      return {
        ...state,
        history: pushHistory(state),
        paths: [...state.paths, action.payload],
        selectedItems: [{ kind: 'PATH', id: action.payload.id }],
        layoutVersion: state.layoutVersion + 1
      };

    case 'UPDATE_PATH':
      return {
        ...state,
        history: pushHistory(state),
        paths: state.paths.map(p => p.id === action.payload.id ? { ...p, ...action.payload.updates } : p),
        layoutVersion: state.layoutVersion + 1
      };

    case 'TRANSFORM_ITEMS': {
      const { items, dx, dy } = action.payload;
      if (dx === 0 && dy === 0) return state;
      const selected = new Set(items.map(item => `${item.kind}:${item.id}`));
      const selectedPathIds = new Set(items.filter(item => item.kind === 'PATH').map(item => item.id));
      const movedNodeIds = new Set(items.filter(item => item.kind === 'NODE').map(item => item.id));
      state.paths.filter(path => selectedPathIds.has(path.id)).forEach(path =>
        path.nodeIds?.forEach(id => { if (id) movedNodeIds.add(id); })
      );
      const nodes = state.nodes.map(node => movedNodeIds.has(node.id) ? { ...node, x: node.x + dx, y: node.y + dy } : node);
      let paths = state.paths.map(path => {
        if (!selectedPathIds.has(path.id)) return path;
        return {
          ...path,
          points: path.points.map((point, index) => path.nodeIds?.[index]
            ? point
            : { x: point.x + dx, y: point.y + dy })
        };
      });
      paths = syncPathPointsToNodes(paths, nodes);
      return {
        ...state,
        history: pushHistory(state),
        nodes,
        paths,
        zones: state.zones.map(zone => selected.has(`ZONE:${zone.id}`) ? {
          ...zone, rects: zone.rects.map(rect => ({ ...rect, x: rect.x + dx, y: rect.y + dy }))
        } : zone),
        icons: state.icons.map(icon => selected.has(`ICON:${icon.id}`) ? { ...icon, x: icon.x + dx, y: icon.y + dy } : icon),
        walls: state.walls.map(wall => selected.has(`WALL:${wall.id}`) ? {
          ...wall, x1: wall.x1 + dx, y1: wall.y1 + dy, x2: wall.x2 + dx, y2: wall.y2 + dy
        } : wall),
        layoutVersion: state.layoutVersion + 1
      };
    }

    case 'RESIZE_ZONE':
      return {
        ...state,
        history: pushHistory(state),
        zones: state.zones.map(zone => zone.id === action.payload.id ? { ...zone, rects: action.payload.rects } : zone),
        layoutVersion: state.layoutVersion + 1
      };

    case 'INSERT_PATH_POINT': {
      const path = state.paths.find(item => item.id === action.payload.pathId);
      if (!path) return state;
      const index = Math.max(0, Math.min(path.points.length, action.payload.index));
      const isSimulation = (path.kind ?? (path.name.toLowerCase().startsWith('sim.') ? 'SIMULATION' : 'ARCHITECTURAL')) === 'SIMULATION';
      const generatedNode = isSimulation ? action.payload.generatedNode : undefined;
      const nodeId = isSimulation ? (action.payload.nodeId ?? generatedNode?.id ?? null) : null;
      const paths = state.paths.map(item => item.id === path.id ? {
        ...item,
        points: [...item.points.slice(0, index), action.payload.point, ...item.points.slice(index)],
        nodeIds: [...(item.nodeIds ?? item.points.map(() => null)).slice(0, index), nodeId, ...(item.nodeIds ?? item.points.map(() => null)).slice(index)],
        generatedNodeIds: generatedNode ? [...(item.generatedNodeIds ?? []), generatedNode.id] : item.generatedNodeIds
      } : item);
      const rebuilt = rebuildSimulationGraph(generatedNode ? [...state.nodes, generatedNode] : state.nodes, paths);
      return {
        ...state,
        history: pushHistory(state),
        ...rebuilt,
        selectedPathPoint: { pathId: path.id, index },
        layoutVersion: state.layoutVersion + 1
      };
    }

    case 'MOVE_PATH_POINT': {
      const path = state.paths.find(item => item.id === action.payload.pathId);
      if (!path || !path.points[action.payload.index]) return state;
      const isSimulation = (path.kind ?? (path.name.toLowerCase().startsWith('sim.') ? 'SIMULATION' : 'ARCHITECTURAL')) === 'SIMULATION';
      const oldNodeId = path.nodeIds?.[action.payload.index] ?? null;
      const nextNodeId = isSimulation ? (action.payload.snapNodeId ?? oldNodeId) : null;
      let nodes = state.nodes;
      if (isSimulation && oldNodeId && !action.payload.snapNodeId) {
        nodes = nodes.map(node => node.id === oldNodeId ? { ...node, x: action.payload.point.x, y: action.payload.point.y } : node);
      }
      const oldGenerated = oldNodeId && path.generatedNodeIds?.includes(oldNodeId) && nextNodeId !== oldNodeId ? [oldNodeId] : [];
      const paths = state.paths.map(item => item.id === path.id ? {
        ...item,
        points: item.points.map((point, index) => index === action.payload.index ? action.payload.point : point),
        nodeIds: (item.nodeIds ?? item.points.map(() => null)).map((id, index) => index === action.payload.index ? nextNodeId : id),
        generatedNodeIds: item.generatedNodeIds?.filter(id => !oldGenerated.includes(id))
      } : item);
      nodes = removeUnreferencedGeneratedNodes(nodes, paths, oldGenerated);
      const rebuilt = rebuildSimulationGraph(nodes, paths);
      return { ...state, history: pushHistory(state), ...rebuilt, layoutVersion: state.layoutVersion + 1 };
    }

    case 'DELETE_PATH_POINT': {
      const path = state.paths.find(item => item.id === action.payload.pathId);
      if (!path || path.points.length <= 2 || !path.points[action.payload.index]) return state;
      const removedNodeId = path.nodeIds?.[action.payload.index] ?? null;
      const generatedCandidates = removedNodeId && path.generatedNodeIds?.includes(removedNodeId) ? [removedNodeId] : [];
      const paths = state.paths.map(item => item.id === path.id ? {
        ...item,
        points: item.points.filter((_, index) => index !== action.payload.index),
        nodeIds: (item.nodeIds ?? item.points.map(() => null)).filter((_, index) => index !== action.payload.index),
        generatedNodeIds: item.generatedNodeIds?.filter(id => !generatedCandidates.includes(id))
      } : item);
      const nodes = removeUnreferencedGeneratedNodes(state.nodes, paths, generatedCandidates);
      const rebuilt = rebuildSimulationGraph(nodes, paths);
      return {
        ...state,
        history: pushHistory(state),
        ...rebuilt,
        selectedPathPoint: null,
        layoutVersion: state.layoutVersion + 1
      };
    }

    case 'PASTE_ITEMS': {
      const payload = action.payload;
      const paths = [...state.paths, ...payload.paths];
      const rebuilt = rebuildSimulationGraph([...state.nodes, ...payload.nodes], paths);
      return {
        ...state,
        history: pushHistory(state),
        ...rebuilt,
        zones: [...state.zones, ...payload.zones],
        walls: [...state.walls, ...payload.walls],
        icons: [...state.icons, ...payload.icons],
        selectedItems: payload.selection,
        selectedPathPoint: null,
        layoutVersion: state.layoutVersion + 1
      };
    }

    case 'DELETE_SELECTION': {
      const selected = new Set(action.payload.map(item => `${item.kind}:${item.id}`));
      const removedPaths = state.paths.filter(path => selected.has(`PATH:${path.id}`));
      const removedNodeIds = new Set(action.payload.filter(item => item.kind === 'NODE').map(item => item.id));
      let paths = state.paths.filter(path => !selected.has(`PATH:${path.id}`));
      const remainingNodeRefs = new Set(paths.flatMap(path => path.nodeIds ?? []).filter((id): id is string => Boolean(id)));
      removedPaths.forEach(path => path.generatedNodeIds?.forEach(id => { if (!remainingNodeRefs.has(id)) removedNodeIds.add(id); }));
      const deletedNodes = state.nodes.filter(node => removedNodeIds.has(node.id));
      paths = removeNodeReferencesFromPaths(paths, deletedNodes);
      let nodes = state.nodes.filter(node => !removedNodeIds.has(node.id));
      const rebuilt = rebuildSimulationGraph(nodes, paths);
      return {
        ...state,
        history: pushHistory(state),
        ...rebuilt,
        zones: state.zones.filter(zone => !selected.has(`ZONE:${zone.id}`)),
        walls: state.walls.filter(wall => !selected.has(`WALL:${wall.id}`)),
        icons: state.icons.filter(icon => !selected.has(`ICON:${icon.id}`)),
        selectedItems: [],
        selectedPathPoint: null,
        layoutVersion: state.layoutVersion + 1
      };
    }

    case 'DELETE_PATH': {
      const deletedPath = state.paths.find(path => path.id === action.payload);
      if (!deletedPath) return state;
      const remainingPaths = state.paths.filter(path => path.id !== action.payload);
      const sharedNodeIds = new Set(remainingPaths.flatMap(path => path.nodeIds ?? []).filter((id): id is string => Boolean(id)));
      const deletedNodeIds = new Set((deletedPath.generatedNodeIds ?? []).filter(id => !sharedNodeIds.has(id)));
      const remainingEdges = new Set(remainingPaths.flatMap(path => path.connections ?? []).flatMap(edge => [
        `${edge.fromId}:${edge.toId}`, `${edge.toId}:${edge.fromId}`
      ]));
      const deletedEdges = new Set((deletedPath.connections ?? [])
        .filter(edge => !remainingEdges.has(`${edge.fromId}:${edge.toId}`))
        .flatMap(edge => [`${edge.fromId}:${edge.toId}`, `${edge.toId}:${edge.fromId}`]));
      return {
        ...state,
        history: pushHistory(state),
        paths: remainingPaths,
        nodes: state.nodes
          .filter(node => !deletedNodeIds.has(node.id))
          .map(node => ({
            ...node,
            connections: node.connections.filter(id => !deletedNodeIds.has(id) && !deletedEdges.has(`${node.id}:${id}`))
          })),
        selectedItems: state.selectedItems.filter(item => item.kind !== 'PATH' || item.id !== action.payload),
        selectedPathPoint: state.selectedPathPoint?.pathId === action.payload ? null : state.selectedPathPoint,
        layoutVersion: state.layoutVersion + 1
      };
    }

    case 'ADD_POINT_TO_PATH': {
      let connection: { fromId: string; toId: string } | null = null;
      const generatedNodeId = action.payload.generatedNode?.id;
      const paths = state.paths.map(path => {
        if (path.id !== action.payload.pathId) return path;
        const previousNodeId = path.nodeIds?.[path.nodeIds.length - 1] ?? null;
        if (previousNodeId && action.payload.nodeId && previousNodeId !== action.payload.nodeId) {
          const existingNode = state.nodes.find(node => node.id === previousNodeId);
          if (!existingNode?.connections.includes(action.payload.nodeId)) {
            connection = { fromId: previousNodeId, toId: action.payload.nodeId };
          }
        }
        return {
          ...path,
          points: [...path.points, action.payload.point],
          nodeIds: [...(path.nodeIds ?? path.points.map(() => null)), action.payload.nodeId ?? null],
          generatedNodeIds: generatedNodeId
            ? [...(path.generatedNodeIds ?? []), generatedNodeId]
            : path.generatedNodeIds,
          connections: connection ? [...(path.connections ?? []), connection] : path.connections
        };
      });
      const baseNodes = action.payload.generatedNode ? [...state.nodes, action.payload.generatedNode] : state.nodes;
      const nodes = connection ? baseNodes.map(node => {
        if (node.id === connection!.fromId && !node.connections.includes(connection!.toId)) {
          return { ...node, connections: [...node.connections, connection!.toId] };
        }
        if (node.id === connection!.toId && !node.connections.includes(connection!.fromId)) {
          return { ...node, connections: [...node.connections, connection!.fromId] };
        }
        return node;
      }) : baseNodes;
      return { ...state, history: pushHistory(state), paths, nodes, layoutVersion: state.layoutVersion + 1 };
    }

    case 'REGION_DELETE': {
      const { x, y, w, h } = action.payload;
      const deletedNodeIds = new Set(
        state.nodes.filter(node => isPointInRect(node, { x, y, w, h })).map(node => node.id)
      );
      const deletedNodes = state.nodes.filter(node => deletedNodeIds.has(node.id));
      const paths = removeNodeReferencesFromPaths(state.paths, deletedNodes).flatMap(path => {
        const kept = path.points.map((point, index) => ({ point, index }))
          .filter(({ point }) => !isPointInRect(point, { x, y, w, h }));
        if (kept.length === 0) return [];
        return [{
          ...path,
          points: kept.map(({ point }) => point),
          nodeIds: kept.map(({ index }) => path.nodeIds?.[index] ?? null)
        }];
      });
      return {
        ...state,
        history: pushHistory(state),
        nodes: state.nodes
          .filter(node => !deletedNodeIds.has(node.id))
          .map(node => ({ ...node, connections: node.connections.filter(id => !deletedNodeIds.has(id)) })),
        zones: state.zones.filter(z => !z.rects.some(r => r.x >= x && r.x + r.w <= x + w && r.y >= y && r.y + r.h <= y + h)),
        icons: state.icons.filter(icon => !isPointInRect({ x: icon.x, y: icon.y }, { x, y, w, h })),
        walls: state.walls.filter(wall => {
          const start = { x: wall.x1, y: wall.y1 };
          const end = { x: wall.x2, y: wall.y2 };
          const corners = [{ x, y }, { x: x + w, y }, { x: x + w, y: y + h }, { x, y: y + h }];
          const crossesBoundary = corners.some((corner, index) =>
            doLineSegmentsIntersect(start, end, corner, corners[(index + 1) % corners.length])
          );
          return !isPointInRect(start, { x, y, w, h }) && !isPointInRect(end, { x, y, w, h }) && !crossesBoundary;
        }),
        paths,
        selectedItems: [],
        layoutVersion: state.layoutVersion + 1
      };
    }

    case 'SET_FLOOR_PLAN': 
      return { ...state, floorPlanImage: action.payload };

    case 'SET_SIM_SPEED':
      return { ...state, simulationSpeed: Math.max(0.1, Math.min(10, action.payload)) };

    case 'SET_AUTO_SPAWN':
      return { 
        ...state, 
        isAutoSpawning: action.payload.active !== undefined ? action.payload.active : state.isAutoSpawning,
        autoSpawnRate: action.payload.rate !== undefined ? action.payload.rate : state.autoSpawnRate
      };

    case 'SET_CALIBRATION':
      return { 
        ...state, 
        calibrationPoints: action.payload.points !== undefined ? action.payload.points : state.calibrationPoints,
        pixelsPerMeter: action.payload.scale !== undefined ? Math.max(1, action.payload.scale) : state.pixelsPerMeter
      };

    case 'SET_MEASUREMENT_POINTS':
      return { ...state, measurementPoints: action.payload };

    case 'SET_MAP_ADJUSTMENTS':
      return {
        ...state,
        mapOpacity: action.payload.opacity ?? state.mapOpacity,
        mapBrightness: action.payload.brightness ?? state.mapBrightness,
        mapContrast: action.payload.contrast ?? state.mapContrast
      };

    case 'SET_HELP_OPEN':
      return { ...state, isHelpOpen: action.payload.open, helpTab: action.payload.tab || state.helpTab };

    case 'SET_REPORT_OPEN':
      return { ...state, isReportOpen: action.payload };

    case 'SET_BEHAVIOR_MODAL_OPEN':
      return { ...state, isBehaviorModalOpen: action.payload };

    case 'TOGGLE_CONTROLS':
      return { ...state, isControlsVisible: !state.isControlsVisible };

    case 'SPAWN_AGENTS':
      return { ...state, agents: [...state.agents, ...action.payload] };

    case 'UPDATE_STATS':
      return { ...state, stats: { ...state.stats, ...action.payload } };

    case 'SET_SIM_CONFIG':
      return { ...state, simConfig: { ...state.simConfig, ...action.payload } };

    case 'SIM_TICK': 
      return { 
        ...state, 
        agents: action.payload.agents, 
        nodes: action.payload.nodes, 
        stats: action.payload.stats 
      };

    case 'TOGGLE_RUN': 
      return { ...state, isRunning: !state.isRunning };

    case 'RESET_SIMULATION':
      return {
        ...state,
        isRunning: false,
        agents: [],
        nodes: state.nodes.map(n => ({ ...n, occupants: [], contamination: 0 })),
        stats: {
          treatedPatients: 0,
          activePatients: 0,
          avgWaitTime: 0,
          avgDistanceTraveled: 0,
          avgDistanceByRole: { patients: 0, doctors: 0, nurses: 0, assistants: 0 },
          staffUtilization: { doctors: 0, nurses: 0, assistants: 0 },
          criticalEvents: 0,
          infections: 0,
          lwbs: 0,
          avgDoorToDoc: 0,
          avgLengthOfStay: 0,
          avgLabTurnaround: 0,
          bedOccupancyRate: 0
        }
      };

    case 'UNDO': {
      if (state.history.length === 0) return state;
      const [lastEntry, ...remainingHistory] = state.history;
      return { 
        ...state, 
        ...lastEntry, 
        history: remainingHistory,
        future: [createHistoryEntry(state), ...state.future].slice(0, 30),
        selectedItems: [],
        selectedPathPoint: null,
        layoutVersion: state.layoutVersion + 1 
      };
    }

    case 'REDO': {
      if (state.future.length === 0) return state;
      const [nextEntry, ...remainingFuture] = state.future;
      return {
        ...state,
        ...nextEntry,
        history: [createHistoryEntry(state), ...state.history].slice(0, 30),
        future: remainingFuture,
        selectedItems: [],
        selectedPathPoint: null,
        layoutVersion: state.layoutVersion + 1
      };
    }

    case 'LOAD_LAYOUT': {
      const payload = action.payload;
      return { 
        ...state, 
        nodes: payload.nodes ?? [],
        zones: payload.zones ?? [],
        walls: payload.walls ?? [],
        paths: payload.paths ?? [],
        icons: payload.icons ?? [],
        pixelsPerMeter: payload.pixelsPerMeter ?? state.pixelsPerMeter,
        floorPlanImage: payload.floorPlanImage !== undefined ? payload.floorPlanImage : state.floorPlanImage,
        mapOpacity: payload.mapOpacity ?? state.mapOpacity,
        mapBrightness: payload.mapBrightness ?? state.mapBrightness,
        mapContrast: payload.mapContrast ?? state.mapContrast,
        simConfig: payload.simConfig ? { ...state.simConfig, ...payload.simConfig } : state.simConfig,
        agents: [], 
        isRunning: false, 
        history: [],
        future: [],
        selectedItems: [],
        selectedPathPoint: null,
        layoutVersion: state.layoutVersion + 1 
      };
    }

    default: 
      console.warn(`Unhandled action type: ${(action as any).type}`);
      return state;
  }
};

const layoutMutationTypes = new Set<AppAction['type']>([
  'ADD_NODE', 'UPDATE_NODE', 'DELETE_NODE', 'ADD_WALL', 'DELETE_WALL', 'ADD_ZONE',
  'UPDATE_ZONE', 'DELETE_ZONE', 'MERGE_ZONES', 'ADD_PATH', 'UPDATE_PATH', 'DELETE_PATH',
  'ADD_POINT_TO_PATH', 'ADD_ICON', 'UPDATE_ICON', 'DELETE_ICON', 'REGION_DELETE',
  'TRANSFORM_ITEMS', 'RESIZE_ZONE', 'INSERT_PATH_POINT', 'MOVE_PATH_POINT',
  'DELETE_PATH_POINT', 'PASTE_ITEMS', 'DELETE_SELECTION'
]);

export const appReducer = (state: AppState, action: AppAction): AppState => {
  const next = reduceApp(state, action);
  if (next === state || !layoutMutationTypes.has(action.type)) return next;
  return { ...next, future: [] };
};
